/** Standalone OTP login page — the entry bundled to login.js.
 *
 * The flow lives in signin/flow.tsx; this just mounts it into the page
 * element and lets it redirect to `next` on success. The edge page is fully
 * static, so `next` / `referral_code` come from the query string.
 *
 * `next` is validated with allowedNextUrl (not sameOriginPath). This page is
 * served from the identity origin (id.<env>), but the flows that land here —
 * checkout, OIDC first-party apps — need to send the user back to a sibling
 * consumer origin (e.g. www.<env>/checkout2/...). allowedNextUrl permits
 * same-origin paths and https targets on the same registrable domain (so
 * `*.muchbetteradventures.com` consumers work) and rejects everything else,
 * exactly as seekr's server-side is_safe_redirect does. A same-origin-only
 * guard here dropped every cross-subdomain next to "/" and stranded users on
 * the id home page after sign-in.
 */

import { createClient } from "../api/client";
import { installGlobalErrorReporting } from "../lib/report";
import { allowedNextUrl } from "../lib/url";
import { mountSignInFlow } from "../signin/flow";
import { knownVariantKey, resolveVariant } from "../signin/variants";

installGlobalErrorReporting("");

const app = document.getElementById("login-app");
if (app) {
  const params = new URLSearchParams(window.location.search);
  // Untrusted key -> a known copy bundle, else the default flow. The key
  // itself rides along to the social-auth begin URL (only if it names a real
  // bundle) so a social sign-up keeps this variant's copy.
  const requestedVariant = params.get("variant");
  const variant = resolveVariant(requestedVariant);
  mountSignInFlow(app, {
    variantKey: knownVariantKey(requestedVariant),
    client: createClient(),
    next:
      allowedNextUrl(
        params.get("next"),
        window.location.origin,
        window.location.hostname,
      ) ?? "/",
    referralCode: params.get("referral_code") ?? "",
    source: params.get("source") ?? undefined,
    wordmarkUrl: "/assets/img/muchbetteradventures.svg",
    ...variant,
  });
}
